import React from 'react';
//internal and external components import
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import { Chip, TableRow, ThemeProvider, Tooltip, createTheme } from '@mui/material';
import { stableSort, getComparator } from '../../common/SortSupport';
import { EnhancedTableHead } from '../EnhancedTableHead';
import { Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faFilePowerpoint, faMagnifyingGlass, faDownload, faLock } from '@fortawesome/free-solid-svg-icons';
import { formatDate, genUID } from '../../common/StaticExports';
import TableSkeleton from '../TableSkeleton';
import NestedThreeDotMenu from '../NestedThreeDotMenu';
import { useDispatch, useSelector } from 'react-redux';
import useFileTranslationProcess from '../../hooks/useFileLangTranslation';
import { useQueryClient, QueryClientProvider } from 'react-query';
import AccessManager from '../../containers/AccessManager';
import { setSnackBarData } from '../../redux/reducers/common';
import { restrictCriteriaUsers, restrictEvidenceUsers, restrictStandardUsers } from '../../api/evaluation';
import { useTranslation } from 'react-i18next';
import { restrictCriteriaUsersLicence, restrictStandardUsersLicence } from '../../api/licence';

const theme = createTheme({
  components: {
    MuiTable: {
      styleOverrides: {
        root: {
          border: 'none',
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '6px 16px',
          border: 0
        }
      }
    },
    MuiStack: {
      styleOverrides: {
        root: {
          display: 'inline',
          width: '90px'
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          minWidth: '90px !important',
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: 12
        }
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          width: 18,
          height: 18
        }
      }
    }
  },
})

const EvidencesTable = (props?: any) => {
    const { data, isLoading, bg, isCriteriaLevel } = props;
    const queryClient = useQueryClient();
    const clubDetails = useSelector((state:any) => state.clubDetails);
    const isAdmin = useSelector((state:any) => state.common.isAdmin);
    const drawer = useSelector((state: any) => state.common.drawer);
    const currentCriteria = useSelector((state:any) => drawer == "Evaluation" ? state.evaluation.selectedSaCriteria : state.licence.selectedLicenceCriteria);
    const currentStandard = useSelector((state:any) => drawer == "Evaluation" ? state.evaluation.selectedSaStandard : state.licence.selectedLicenceStandard);
    const initialAccessState = {
      isOpen: false,
      itemId: null,
    };
    const [manageAccess, setManageAccess] = React.useState(initialAccessState);
    const dispatch = useDispatch();
    const [t] = useTranslation();

  //supporting contents
  const [order, setOrder] = React.useState('');
  const [orderBy, setOrderBy] = React.useState('');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const visibleRows:any = React.useMemo(
    () =>
      data?.length > 0 &&
      stableSort(data, getComparator(order, orderBy)),
    [order, orderBy, data],
  );

  const head = isCriteriaLevel ?
  [
    {key: 'name', displayName: 'Title'},
    {key: 'uploaded', displayName: 'Upload Date'},
    {key: '', displayName: 'Standards'},
    {key: '', displayName: ''},
  ] : 
  [
    {key: 'name', displayName: 'Title'},
    {key: 'uploaded', displayName: 'Upload Date'},
    {key: '', displayName: ''},
  ]

  const onOpenFile = useFileTranslationProcess({
    onSuccessTranslation: () => {
      queryClient.invalidateQueries(['evidence_library', { clubId: clubDetails.id }]);  // Invalidate the query
    },
  });
  

  const threeDotMenuOptions = 
  [
    {label: 'View File', icon: faMagnifyingGlass, isDisabled: false},
    {label: 'Download File', icon: faDownload, isDisabled: false},
    { label: "Manage Access", icon: faLock, isDisabled: false },
  ];
  const handleThreedotMenuClick = (selctedAction:any, row:any) => {
    switch(selctedAction) {
      case 'View File':
        onOpenFile('preview', row);
        break;
      case 'Download File':
        onOpenFile('download', row);
        break;
      case "Manage Access":
        const tempAccessData = {
          isOpen: true,
          itemId: row?.id,
        }
        setManageAccess(tempAccessData);
        break;
    }
  }

  const manageUserAccess = (isUpdated: any, restrictedUsers:any, newUnRestrictedUsers?: any) => {
    if (isUpdated) {
            if (newUnRestrictedUsers && newUnRestrictedUsers?.length > 0 && isCriteriaLevel) {
              var criteriaRestrictedUserIds = currentCriteria?.restrictions?.filter((res:any) => res.isRestricted)?.map((x:any) => x.userId) || [];
              var updatedCriteriaUsers = newUnRestrictedUsers ? criteriaRestrictedUserIds?.filter((num:any) => !newUnRestrictedUsers?.includes(num)) : restrictedUsers;
              drawer == "Evaluation" ? 
              restrictCriteriaUsers({clubId: clubDetails?.id, criteriaId: currentCriteria?.id, users: updatedCriteriaUsers}) :
              restrictCriteriaUsersLicence({clubId: clubDetails?.id, criteriaId: currentCriteria?.id, users: updatedCriteriaUsers});
            }
            if (newUnRestrictedUsers && newUnRestrictedUsers?.length > 0 && !isCriteriaLevel) {
              var standardsRestrictedUserIds = currentStandard?.restrictions?.filter((res:any) => res.isRestricted)?.map((x:any) => x.userId) || [];
              var updatedStandardUsers = newUnRestrictedUsers ? standardsRestrictedUserIds?.filter((num:any) => !newUnRestrictedUsers?.includes(num)) : restrictedUsers;
              drawer == "Evaluation" ? 
              restrictStandardUsers({clubId: clubDetails?.id, criteriaId: currentCriteria?.id, users: updatedStandardUsers}) :
              restrictStandardUsersLicence({clubId: clubDetails?.id, criteriaId: currentCriteria?.id, users: updatedStandardUsers});
            }
        restrictEvidenceUsers({clubId: clubDetails?.id, id: manageAccess?.itemId, users: restrictedUsers})
        .then(() => {
        })
        .catch((error:any) => {
          const warningData: any = {
            isOpen: true,
            severity: "error",
            message: error?.message || t("ACCESS_UPDATE_FAILED"),
          };
          dispatch(setSnackBarData(warningData));
        });
    }
    setManageAccess(initialAccessState);
  };

  //table contents
    return(
        <React.Fragment>
          <ThemeProvider theme={theme}>
          <Box sx={{ position: 'relative', height: '80vh', overflowY: 'auto' }}>
                <AccessManager
                  isOpen={manageAccess?.isOpen}
                  handleIsOpen={manageUserAccess}
                  from={"evidences"}
                  itemId={manageAccess?.itemId}
                  criteriaRestrictions={isCriteriaLevel ? currentCriteria?.restrictions : currentStandard && currentStandard?.restrictions ? [...currentCriteria?.restrictions, ...currentStandard?.restrictions] : currentCriteria?.restrictions}
                />
              <TableContainer sx={{ overflowY: 'auto' }}>
                <Table stickyHeader size="small" aria-label="a dense table">
                    <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    tableHeads={head}
                    bg={bg}
                    />
                    <TableBody>
                    {
                      !isLoading && visibleRows && visibleRows?.length ?
                      visibleRows?.map((d: any, i: number) => (
                        <TableRow
                        sx={{ cursor: 'pointer' }}
                        key={`history-table-row-${i}`}
                        >
                            <TableCell className='fs12'sx={{ width: '70%' }}>
                                <FontAwesomeIcon className={'smallFileIcon'} icon={(d.type === '.xlsx') ? faFileExcel : faFilePowerpoint} />
                                {d?.name || d?.title}
                            </TableCell>
                            <TableCell className='fs12'>
                              {formatDate(d.uploaded)}
                            </TableCell>
                            {
                              isCriteriaLevel &&
                              <TableCell className='fs12' sx={{ minWidth: 180 }}>
                                {
                                  drawer == "Evaluation" &&
                                  d?.licenceStandards?.length > 0 ?
                                  (d.licenceStandards?.length == 1 ?
                                  <Chip className='chipStyle' label={genUID(d.licenceStandards[0].uid)} color="primary" /> : 
                                  <Box>
                                    <Chip className='chipStyle' label={genUID(d.licenceStandards[0].uid)} color="primary" />
                                    <Tooltip title={d.licenceStandards.map((obj:any) => genUID(obj.uid)).join(', ')} arrow placement='top'>
                                      <Chip className='chipStyle' sx={{ ml: '0px !important' }} label={'+' + (d.licenceStandards.length - 1)} color="primary" />
                                    </Tooltip>
                                  </Box>
                                  ) : 
                                  drawer == "Licence" &&
                                  d?.licenceStandards?.length > 0 ?
                                  (d.licenceStandards?.length == 1 ?
                                  <Chip className='chipStyle' label={genUID(d.licenceStandards[0].uid)} color="primary" /> : 
                                  <Box>
                                    <Chip className='chipStyle' label={genUID(d.licenceStandards[0].uid)} color="primary" />
                                    <Tooltip title={d.licenceStandards.map((obj:any) => genUID(obj.uid)).join(', ')} arrow placement='top'>
                                      <Chip className='chipStyle' sx={{ ml: '0px !important' }} label={'+' + (d.licenceStandards.length - 1)} color="primary" />
                                    </Tooltip>
                                  </Box>
                                  ) : ''
                                }
                              </TableCell>
                            }
                            <TableCell className='fs12' align={'right'}>
                              {
                                !isAdmin &&
                                <NestedThreeDotMenu options={threeDotMenuOptions} manageClick={(e:any) => {handleThreedotMenuClick(e, d)}} />
                              }
                            </TableCell>
                        </TableRow>
                    )) :
                    (
                      !isLoading && (data?.length == 0 || data == undefined) ?
                      <TableRow>
                        <TableCell colSpan={head?.length} className='fs12' sx={{textAlign:'center'}}>No Records Found</TableCell>
                      </TableRow>:
                      <TableSkeleton rowCount={6} cellCount={head?.length} />
                    )
                  }
                    </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </ThemeProvider>
        </React.Fragment>
    )
}

export default EvidencesTable;